import {  t  } from "@/client/libs/dummy-i18n";
import { 
  MagnifyingGlassIcon,
  PlusCircleIcon, 
  PlusIcon,
  XIcon
} from "@phosphor-icons/react";
import type {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  Skill,
  Volunteer,
} from "@stackmorph/schema";
import { Button, Input, ScrollArea, Separator } from "@stackmorph/ui";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { Icon } from "@/client/components/icon";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useResumeStore } from "@/client/stores/resume";

import { BasicsSection } from "./sections/basics";
import { SectionBase } from "./sections/shared/section-base";
import { SectionIcon } from "./sections/shared/section-icon";
import { SummarySection } from "./sections/summary";

export const LeftSidebar = () => {
  const containterRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState("");

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => state.resume.data.sections.custom);

  const expandAllSections = useResumeStore((state) => state.expandAllSections);
  const collapseAllSections = useResumeStore((state) => state.collapseAllSections);

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sections: { id: import("@stackmorph/schema").SectionKey; name: string; component: React.ReactNode }[] = [
    { id: "basics", name: t`Basics`, component: <BasicsSection /> },
    { id: "summary", name: t`Summary`, component: <SummarySection /> },
    { id: "profiles", name: t`Profiles`, component: <SectionBase<Profile> id="profiles" title={(item) => item.network} description={(item) => item.username} /> },
    { id: "experience", name: t`Experience`, component: <SectionBase<Experience> id="experience" title={(item) => item.company} description={(item) => item.position} /> },
    { id: "education", name: t`Education`, component: <SectionBase<Education> id="education" title={(item) => item.institution} description={(item) => item.area} /> },
    { id: "skills", name: t`Skills`, component: <SectionBase<Skill> id="skills" title={(item) => item.name} description={(item) => item.description || (item.keywords.length > 0 ? `${item.keywords.length} keywords` : undefined)} /> },
    { id: "languages", name: t`Languages`, component: <SectionBase<Language> id="languages" title={(item) => item.name} description={(item) => item.description} /> },
    { id: "awards", name: t`Awards`, component: <SectionBase<Award> id="awards" title={(item) => item.title} description={(item) => item.awarder} /> },
    { id: "certifications", name: t`Certifications`, component: <SectionBase<Certification> id="certifications" title={(item) => item.name} description={(item) => item.issuer} /> },
    { id: "interests", name: t`Interests`, component: <SectionBase<Interest> id="interests" title={(item) => item.name} description={(item) => item.keywords.length > 0 ? `${item.keywords.length} keywords` : undefined} /> },
    { id: "projects", name: t`Projects`, component: <SectionBase<Project> id="projects" title={(item) => item.name} description={(item) => item.description} /> },
    { id: "publications", name: t`Publications`, component: <SectionBase<Publication> id="publications" title={(item) => item.name} description={(item) => item.publisher} /> },
    { id: "volunteer", name: t`Volunteer`, component: <SectionBase<Volunteer> id="volunteer" title={(item) => item.organization} description={(item) => item.position} /> },
    { id: "references", name: t`References`, component: <SectionBase<Reference> id="references" title={(item) => item.name} description={(item) => item.description} /> },
  ];

  const filteredSections = useMemo(() => {
    if (!search) return sections;
    return sections.filter((section) => section.name.toLowerCase().includes(search.toLowerCase()));
  }, [search, sections]);

  return (
    <div className="flex h-full bg-secondary-accent/30">
      {/* Quick Nav Sidebar (Icons) */}
      <div className="hidden basis-14 flex-col items-center justify-between border-r border-border/50 bg-secondary-accent/30 py-4 sm:flex">
        <Button asChild size="icon" variant="ghost" className="size-10 rounded-full">
          <Link to="/dashboard">
            <Icon size={18} />
          </Link>
        </Button>

        <ScrollArea className="flex-1 w-full px-2 py-4 hide-scrollbar">
          <div className="flex flex-col items-center justify-center gap-y-3">
            {sections.map((section) => (
              <SectionIcon
                key={section.id}
                id={section.id}
                name={section.name}
                onClick={() => { scrollIntoView(`#${section.id}`); }}
              />
            ))}

            <Separator className="w-8" />

            <SectionIcon
              id="custom"
              variant="outline"
              name={t`Add a new section`}
              icon={<PlusIcon size={14} />}
              onClick={() => {
                addSection();
                setTimeout(() => { scrollIntoView("& > section:last-of-type"); }, 100);
              }}
            />
          </div>
        </ScrollArea>

        <UserOptions>
          <Button size="icon" variant="ghost" className="rounded-full size-10">
            <UserAvatar size={32} />
          </Button>
        </UserOptions>
      </div>

      {/* Content Editor Area */}
      <div className="flex flex-1 flex-col">
        {/* Search Bar */}
        <div className="sticky top-0 z-10 border-b border-border/50 bg-background/80 p-4 backdrop-blur-md">
          <div className="relative">
            <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              placeholder={t`Search sections...`} 
              className="pl-9 bg-secondary/50 border-transparent focus:border-primary focus:bg-background transition-all"
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
            />
            {search && (
              <button 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => { setSearch(""); }}
              >
                <XIcon size={14} />
              </button>
            )}
          </div>
        </div>

        <ScrollArea orientation="vertical" className="flex-1 pb-16 lg:pb-0">
          <div ref={containterRef} className="grid gap-y-8 p-6 @container/left">
            <AnimatePresence>
              {filteredSections.map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {section.component}
                  {index < filteredSections.length - 1 && <Separator className="mt-8" />}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Custom Sections - Always show if not searching or if matches */}
            {Object.values(customSections).map((section) => (
              <Fragment key={section.id}>
                <Separator />
                <SectionBase<CustomSection>
                  id={`custom.${section.id}`}
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
              </Fragment>
            ))}

            {!search && (
              <>
                <Separator />
                <div className="flex flex-col gap-4 pt-4">
                  <Button size="lg" variant="outline" className="border-dashed" onClick={addSection}>
                    <PlusCircleIcon size={18} />
                    <span className="ml-2">{t`Add a new section`}</span>
                  </Button>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Button variant="ghost" size="sm" onClick={expandAllSections}>{t`Expand All`}</Button>
                    <Button variant="ghost" size="sm" onClick={collapseAllSections}>{t`Collapse All`}</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
